// src/encounter/ap-order-row.tsx
// ONE ORDER ROW on the A&P canvas — since 2026-09-23 a THIN WRAPPER over the Signal DS
// `ApOrderCard`.
//
// This row was built local on 2026-08-06 (Andrew's override: "match the design exactly, ignore
// Signal DS constraints"), because `ApOrderCard` could not express it then: a hardcoded
// surface-card fill, one identity token for all six types, and a tiled glyph. It was then brought
// to DEPLOYED Flutter parity on 2026-09-08 (no type chip, no edit pencil — Ahmed's instruction) and
// measured against `ap_item_detail_cell.dart` / `text_selection_widget.dart` under R9.
//
// On 2026-09-23 that anatomy was graduated INTO the DS as `ApOrderCard`'s default render (Ahmed:
// "the work done in PA since 2026-09-01 is up to date; use it in Signal DS and reuse it in PA
// from Signal DS"): the grey-100 panel, the inset 2px per-type `ORDER_TYPE_BAR` ramp, the 20px
// untiled glyph, the 15px name / 13px bold hint code, the `signals` / `actions` slots, the linked
// Dx code line and the outlined error bin. This file keeps its PUBLIC PROPS so no caller changes,
// and maps them onto the DS card.
//
// Two mapping decisions, stated:
//   · `dxCodes: string[]` becomes the card's `diagnoses` + `linkedDxIds`, one positional id per
//     code (`dx-0`, `dx-1`, …) so the ids are unique by construction and `linkedDxIds` keeps the
//     link ORDER — first = the order's primary reason, drawn bold in brand.
//   · `onEdit` is forwarded as the DS card's `onOpen`, NEVER as its `onEdit`. The deployed row
//     draws no pencil (2026-09-08, Flutter parity), and that left the A/P with no way to edit an
//     order. Andrew's decision, 2026-09-30 (PROD-2717): "remove the pencil but open the edit by
//     pressing over the cards — in a/p cards of selected orders". So pressing anywhere on the card
//     (click, or Enter/Space on its overlay button named `Edit <name>`) reopens the order's dialog,
//     and still no pencil is drawn. The bin, the Dx-link line and the `actions` cluster keep their
//     own actions. This supersedes the pencil drawn on design board 4. Hosts gate read-only by not
//     passing `onEdit` (a read-only encounter never opens).
//
// `ApMedLifecycleActions` below is still local: it is the host's `actions` content, not the card.
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { ApOrderCard, type ApOrderCardDx, type OrderType } from '@scripsteam/scrips-signal-ds';
import { Ban, Pencil, RefreshCw, Trash2 } from 'lucide-react';

export interface ApOrderRowProps {
  type: OrderType;
  /** Order display name, e.g. "Metabolic panel total ca". */
  name: string;
  /** The order's OWN code (LOINC / CPT / CVX / DDC …), inline-baseline after the name. */
  code?: string;
  /** Optional detail/SIG line beneath the name. */
  sig?: string;
  /**
   * The linked diagnosis CODES, in link order — first = the order's primary reason (bold, brand),
   * the rest additional links (muted). Empty → the line is omitted.
   */
  dxCodes?: string[];
  /**
   * Re-link the order. The Dx codes are STATIC text, which on its own would delete the capability
   * the DS `DxLinkChip`s carried (each chip toggled a link). So the whole code group becomes ONE
   * operable target that opens the host's link editor. Absent → the codes render as plain text.
   */
  onEditDxLinks?: () => void;
  /**
   * Reopen the order's dialog pre-filled, by pressing the card (PROD-2717). Forwarded as the DS
   * card's pencil-less `onOpen` — see the file header. Absent → the card is not pressable.
   */
  onEdit?: () => void;
  /**
   * Also draw the DS card's edit pencil, next to the remove bin at the row's right edge, calling the same `onEdit` (the frame v2
   * Plan, export 21-10 ApOrderRow). The card stays pressable. Default off: every other host keeps PROD-2717's pencil-less row.
   */
  editPencil?: boolean;
  onRemove?: () => void;
  /**
   * Row-level clinical signals rendered at the end of the head line (the STAT priority chip and
   * the drug-interaction glyph — Flutter's `additionalWidget`, assessment_plan_screen.dart:823).
   */
  signals?: ReactNode;
  /** Trailing cluster, before the remove bin — the ACTIVE MEDICATIONS action stack. */
  actions?: ReactNode;
  'aria-label'?: string;
}

/** `dxCodes` → the DS card's diagnosis pool, one positional id per code. */
function toDxPool(codes: string[]): ApOrderCardDx[] {
  return codes.map((c, i) => ({ id: `dx-${i}`, code: c, display: c }));
}

export function ApOrderRow({
  type,
  name,
  code,
  sig,
  dxCodes,
  onEditDxLinks,
  onEdit,
  editPencil = false,
  onRemove,
  signals,
  actions,
  'aria-label': ariaLabel,
}: ApOrderRowProps) {
  const diagnoses = toDxPool(dxCodes ?? []);
  return (
    <ApOrderCard
      type={type}
      name={name}
      code={code}
      detail={sig}
      dxMode="linked"
      diagnoses={diagnoses}
      linkedDxIds={diagnoses.map((d) => d.id)}
      onEditDxLinks={onEditDxLinks}
      onOpen={onEdit}
      onEdit={editPencil ? onEdit : undefined}
      onRemove={onRemove}
      signals={signals}
      actions={actions}
      aria-label={ariaLabel}
    />
  );
}

// ── ACTIVE MEDICATIONS action stack ─────────────────────────────────────────────────────────────
// R9 — `active_medication_row_widget.dart:361, 384, 407` draws all three lifecycle glyphs at 24;
// the design node's 15 made the whole cluster read as disabled chrome.

/* R9 — `active_medication_row_widget.dart:374, 397, 420` puts `horizontalSpace24` between the
   three lifecycle actions; 4 came off the design node and packed them into a single blur. */
const ACTION_CLUSTER = 'flex items-center gap-6 px-3';
/* R9 — `active_medication_row_widget.dart:370, 393, 416, 437`: the action labels are 11px w400.
   The button's own colour is the action's TONE (below), which the label inherits. */
const ACTION_BTN =
  'flex flex-col items-center gap-1 py-1.5 px-0 border-none rounded-sm bg-transparent font-sans ' +
  'text-size-xs font-regular leading-compact';
/** The two tones the cluster uses — brand for Change/Refill; Discontinue/Delete neutral at rest, red only on hover
 *  (icon pass, 16 Oct: no decorative red, an idle destructive action is neutral). */
const TONE_CLASS = {
  brand: 'text-brand-primary',
  error: 'text-text-secondary hover:text-status-error',
} as const;

function MedAction({
  label,
  glyph,
  tone,
  onClick,
}: {
  label: string;
  glyph: ReactNode;
  tone: keyof typeof TONE_CLASS;
  /** Absent ⇒ the button renders DISABLED. Visible per the design, never live-looking-but-dead. */
  onClick?: () => void;
}) {
  const live = !!onClick;
  return (
    <button
      type="button"
      disabled={!live}
      onClick={onClick}
      title={live ? label : `${label} — not available yet`}
      className={cn(
        ACTION_BTN,
        label === 'Discontinue' ? 'w-18' : 'w-14',
        TONE_CLASS[tone],
        live ? 'cursor-pointer opacity-100' : 'cursor-not-allowed opacity-45',
      )}
    >
      {glyph}
      <span>{label}</span>
    </button>
  );
}

export interface ApMedLifecycleActionsProps {
  /**
   * All four render, always — the design's cluster is fixed (:745-762). A handler-less action is
   * DISABLED, not hidden: the row's shape stays honest to the design while no button is ever bound
   * to a no-op. Today only `onDiscontinue` has a real write behind it in this repo
   * (services/use-save-medication.ts `buildStopMedicationBody` → PostStopMedication); Change
   * (PUT Update/{patientRxOrderId}), Refill (PostRefill) and Delete exist on
   * Scrips.Patient EPriscriptionController but have no React hook yet.
   */
  onChange?: () => void;
  onRefill?: () => void;
  onDiscontinue?: () => void;
  onDelete?: () => void;
}

export function ApMedLifecycleActions({ onChange, onRefill, onDiscontinue, onDelete }: ApMedLifecycleActionsProps) {
  return (
    <div className={ACTION_CLUSTER}>
      <MedAction
        label="Change"
        tone="brand"
        glyph={<Pencil className="size-5" aria-hidden />}
        onClick={onChange}
      />
      <MedAction
        label="Refill"
        tone="brand"
        glyph={<RefreshCw className="size-5" aria-hidden />}
        onClick={onRefill}
      />
      <MedAction
        label="Discontinue"
        tone="error"
        glyph={<Ban className="size-5" aria-hidden />}
        onClick={onDiscontinue}
      />
      <MedAction
        label="Delete"
        tone="error"
        glyph={<Trash2 className="size-5" aria-hidden />}
        onClick={onDelete}
      />
    </div>
  );
}
